import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FacilityItemInformation } from 'src/app/common/models/facilityItemInformation';
import { ItemFilter, AvailabilityOption } from 'src/app/common/models/itemFilter';
import { ItemsService } from 'src/app/services/items.service';

import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-manage-item-filter',
  templateUrl: './manage-item-filter.component.html',
  styleUrls: ['./manage-item-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FormsModule],
})
export class ManageItemFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public items: FacilityItemInformation[] = [];
  @Input() public filteredItems: FacilityItemInformation[] = [];
  @Input() public pageName = '';

  @Output() public filteredItemsEvent = new EventEmitter<FacilityItemInformation[]>();
  public departments: string[] = [];
  public locations: string[] = [];
  public filters: ItemFilter;

  public availabilityOptions: AvailabilityOption[] = [
    {
      value: 'All',
      description: 'Both Active & Inactive ' + this.pageName,
      testFunction: function () {
        return true;
      },
    },
    {
      value: 'Active',
      description: 'Only Active ' + this.pageName,
      testFunction: function (i: FacilityItemInformation) {
        return i.isAvailable;
      },
    },
    {
      value: 'Inactive',
      description: 'Only Inactive ' + this.pageName,
      testFunction: function (i: FacilityItemInformation) {
        return !i.isAvailable;
      },
    },
  ];

  private urlUpdated = function (updateEvent: HashChangeEvent): void {
    window.location.reload();
  };

  constructor(
    private itemService: ItemsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    // add this here if user pastes url or updates query strings directly
    addEventListener('hashchange', this.urlUpdated);
    this.filters = this.getFilterDefaults();

    this.populateFilters();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.filterSetup();
      this.updateFilteredItems();
    }
  }

  public ngOnInit(): void {
    this.filterSetup();
  }

  private filterSetup(): void {
    this.filters = this.getFilterDefaults();
    this.populateFilters();
    this.activatedRoute.queryParams.subscribe((params) => {
      this.filters.textSearch = params['search'];
      this.filters.departmentName = params['department'] || '';
      this.filters.productType = params['product'] || '';
      this.filters.availability = params['availability'] || 'All';
    });
  }

  private newFilteredItemsArray(value: FacilityItemInformation[]): void {
    // pass filtered items back up to change event
    this.filteredItemsEvent.emit(value);
  }

  private updateQueryStrings(filterValues: ItemFilter): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        search: filterValues.textSearch,
        department: filterValues.departmentName,
        product: filterValues.productType,
        availability: filterValues.availability,
      },
      queryParamsHandling: 'merge',
    });
  }

  public updateFilteredItems(): void {
    // Create an array of searchable terms for the text search
    const searchTextChunks = this.getTextInputChunks(this.filters.textSearch);

    const availabilityOption =
      this.availabilityOptions
      && this.availabilityOptions.find((o) => {
        return o.value === this.filters.availability;
      });

    // Iterate through each item and do the filtering
    this.filteredItems = this.items.filter((item) => {
      let keepThisItem = true;

      // If there is an availability specified, ensure the item matches it
      if (availabilityOption) {
        keepThisItem = keepThisItem && availabilityOption.testFunction.call(this, item);
      }

      // If the user has a department selected, apply this filter
      if (this.filters.departmentName) {
        keepThisItem = keepThisItem && this.filters.departmentName === item.departmentName;
      } else if (this.filters.productType) {
        keepThisItem = keepThisItem && this.filters.productType === item.productType;
      }

      // If the user has a text search, apply this filter
      if (searchTextChunks && searchTextChunks.length > 0) {
        searchTextChunks.forEach(function (text) {
          // For each search term entered, ensure it matches either the
          // item description, item name or the commerce number.
          keepThisItem =
            keepThisItem
            && ((item.externalItemNumber || '').toLowerCase().startsWith(text)
              || (item.commerceItemNumber || '').toLowerCase().startsWith(text)
              || (item.itemDescription || '').toLowerCase().indexOf(text) > -1
              || (item.itemName || '').toLowerCase().indexOf(text) > -1);
        });
      }

      return keepThisItem;
    });

    this.newFilteredItemsArray(this.filteredItems);
    this.updateQueryStrings(this.filters);
  }

  private getTextInputChunks(textSearch: string): string[] {
    if (!textSearch) {
      return [];
    }

    return textSearch
      .trim() // Remove trailing spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with a single one
      .toLowerCase() // To lower case for comparing later on
      .split(' '); // Split into chunks via space character
  }

  private getFilterDefaults(): ItemFilter {
    const resetFilter: ItemFilter = {
      textSearch: '',
      departmentName: '',
      productType: '',
      availability: this.availabilityOptions[0].value,
    };

    return resetFilter;
  }

  private populateFilters(): void {
    if (this.pageName === 'Items') {
      this.departments = this.itemService.sortItemsByFilter(this.items, 'departmentName');
    } else if (this.pageName === 'Modifiers') {
      this.locations = this.itemService.sortItemsByFilter(this.items, 'productType');
    }
  }

  public ngOnDestroy(): void {
    removeEventListener('hashchange', this.urlUpdated);
  }
}
